import React, { useState } from 'react';
import {
  Container,
  Box,
  Card,
  CardMedia,
  CardContent,
  Button,
  Typography,
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import {
  Star as StarIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { useNavigate } from 'react-router-dom';
// Adicionar imports para tipagem do sx
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { AddWishlistItemDialog } from '../components/wishlist/AddWishlistItemDialog';
import { EditWishlistItemDialog } from '../components/wishlist/EditWishlistItemDialog';
import { DeleteConfirmDialog } from '../components/wishlist/DeleteConfirmDialog';
import { useWishlist, WishlistItem } from '../hooks/useWishlist';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useSafeArea } from '../hooks/useSafeArea';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

export const WishlistPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { items, loading, addItem, removeItem, updateItem } = useWishlist();
  const { bottom } = useSafeArea();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [purchaseFilter, setPurchaseFilter] = useState<'all' | 'purchased' | 'pending'>('all');

  const purchasedCount = items.filter((i) => i.purchased).length;
  const pendingCount = items.length - purchasedCount;
  const displayItems = purchaseFilter === 'all'
    ? items
    : items.filter((i) => (purchaseFilter === 'purchased' ? !!i.purchased : !i.purchased));

  // Função para abrir URLs usando Capacitor Browser no mobile
  const openUrl = async (url: string) => {
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  const [editDialog, setEditDialog] = useState<{ open: boolean; item: WishlistItem | null }>({
    open: false,
    item: null,
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: WishlistItem | null }>({
    open: false,
    item: null,
  });

  // Extrair e "normalizar" valores do tema para evitar tipos-união complexos do TS
  const bg = String((theme as any)?.colors?.background ?? '');
  const cardBg = String((theme as any)?.colors?.card ?? '');
  const borderColor = String((theme as any)?.colors?.border ?? '');
  const primaryColor = String((theme as any)?.colors?.primary ?? '');
  const textPrimary = String((theme as any)?.colors?.text?.primary ?? '');
  const radiusMedium = (theme as any)?.radii?.medium ?? (theme as any)?.radii ?? 0;

  // Declarar objetos sx tipados para evitar inferência complexa
  const boxSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: bg,
    minHeight: '100vh',
    paddingTop: '70px',
  };

  const containerSx: SxProps<Theme> = { mt: 2, flex: 1, pb: `${100 + bottom}px` };

  const topButtonSx: SxProps<Theme> = {
    backgroundColor: theme.colors.secondary,
    color: theme.colors.text.onSecondary,
    borderRadius: radiusMedium,
    padding: '12px',
    fontSize: '0.875rem',
    fontWeight: 'bold',
    textTransform: 'none',
    marginBottom: 3,
    boxShadow: theme.shadows?.small || '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': { backgroundColor: theme.colors.secondary, opacity: 0.9 },
  };

  const titleSx: SxProps<Theme> = {
    color: textPrimary,
    fontWeight: 'bold',
    fontSize: '1.125rem',
  };

  const cardSx: SxProps<Theme> = {
    borderRadius: radiusMedium,
    boxShadow: 'none',
    backgroundColor: cardBg,
    border: `1px solid ${borderColor}`,
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': { transform: 'scale(1.02)' },
  };

  const cardMediaSx: SxProps<Theme> = {
    aspectRatio: '1 / 1',
    objectFit: 'cover',
  };

  const iconSx: SxProps<Theme> = {
    fontSize: '0.875rem',
  };

  const cardContentSx: SxProps<Theme> = {
    padding: 1,
    '&:last-child': { paddingBottom: 1 },
    backgroundColor: cardBg,
  };

  const titleOverlaySx: SxProps<Theme> = {
    color: textPrimary,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const actionsContainerSx: SxProps<Theme> = {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    gap: 0.5,
    // Sempre visível em telas sensíveis ao toque (ou por padrão para simplificar)
    // Usando background sólido para garantir contraste
    backgroundColor: theme.colors.background,
    borderRadius: radiusMedium,
    padding: '4px',
    boxShadow: theme.shadows?.small || '0 2px 4px rgba(0,0,0,0.1)',
    zIndex: 2, // Garantir que fique sobre a imagem

  };

  const actionIconSx: SxProps<Theme> = {
    color: textPrimary,
    fontSize: '1.25rem',
    padding: '4px',
    '&:hover': {
      color: primaryColor,
    },
  };

  const handleEditClick = (item: WishlistItem) => {
    setEditDialog({ open: true, item });
  };

  const handleDeleteClick = (item: WishlistItem) => {
    setDeleteDialog({ open: true, item });
  };

  const handleConfirmDelete = async () => {
    if (deleteDialog.item) {
      try {
        await removeItem(deleteDialog.item.id);
        setDeleteDialog({ open: false, item: null });
      } catch (error) {
        console.error('Erro ao excluir item:', error);
        // Manter dialog aberto em caso de erro
      }
    }
  };

  const handleSaveEdit = async (itemId: string, updates: { title: string; notes?: string; store?: string; price?: number }) => {
    await updateItem(itemId, updates);
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <>
      <TopBar
        title="Wishlist"
        icon={<StarIcon sx={iconSx} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        onBackClick={() => navigate(-1)}
        onSearchClick={() => {
          const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
      />

      <Box sx={boxSx}>
        <Container sx={containerSx}>
          <Button
            startIcon={<AddIcon />}
            fullWidth
            onClick={() => setDialogOpen(true)}
            sx={topButtonSx}
          >
            Adicionar Item
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              component="h1"
              sx={titleSx}
            >
              {items.length > 0 ? 'Veja sua lista de desejos:' : 'Sua lista de desejos está vazia'}
            </Typography>
            {items.length > 0 && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, value) => value && setViewMode(value)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    border: `1px solid ${borderColor}`,
                    color: theme.colors.text.secondary,
                    padding: '4px 8px',
                    '&.Mui-selected': {
                      backgroundColor: `${primaryColor}20`,
                      color: primaryColor,
                      '&:hover': {
                        backgroundColor: `${primaryColor}30`,
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="list" aria-label="list view">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="grid view">
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {items.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {([
                { key: 'all', label: `Todos (${items.length})` },
                { key: 'pending', label: `Ainda não comprados (${pendingCount})` },
                { key: 'purchased', label: `Já comprados (${purchasedCount})` },
              ] as const).map((opt) => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  size="small"
                  onClick={() => setPurchaseFilter(opt.key)}
                  variant={purchaseFilter === opt.key ? 'filled' : 'outlined'}
                  sx={purchaseFilter === opt.key
                    ? { backgroundColor: primaryColor, color: theme.colors.text.contrast }
                    : { borderColor, color: theme.colors.text.secondary }}
                />
              ))}
            </Box>
          )}

          {items.length > 0 ? (
            viewMode === 'grid' ? (
              /* Grid View (original) */
              <Grid container spacing={2}>
                {displayItems.map((item) => (
                  <Grid
                    size={{ xs: 6, md: 4, lg: 3 }}
                    key={item.id}
                  >
                    <Card sx={{ ...cardSx, position: 'relative' }}>
                      {item.purchased && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            left: -28,
                            zIndex: 3,
                            transform: 'rotate(-45deg)',
                            backgroundColor: primaryColor,
                            color: theme.colors.text.contrast,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            textAlign: 'center',
                            width: 110,
                            py: 0.4,
                            pointerEvents: 'none',
                          }}
                        >
                          Já é meu!
                        </Box>
                      )}
                      {/* Ações de edição/exclusão */}
                      <Box sx={actionsContainerSx}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(item);
                          }}
                        >
                          <EditIcon sx={actionIconSx} />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(item);
                          }}
                        >
                          <DeleteIcon sx={actionIconSx} />
                        </IconButton>
                      </Box>

                      {/* Imagem do item */}
                      {item.imageUrl ? (
                        <CardMedia
                          component="img"
                          image={item.imageUrl}
                          alt={item.title || 'Item da wishlist'}
                          sx={{
                            ...cardMediaSx,
                            objectFit: item.imageUrl.includes('favicon') ? 'scale-down' : 'cover',
                            backgroundColor: item.imageUrl.includes('favicon') ? theme.colors.background : 'transparent',
                            cursor: item.url ? 'pointer' : 'default',
                            '&:hover': item.url ? {
                              opacity: 0.9,
                              transition: 'opacity 0.2s',
                            } : {},
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.url) {
                              openUrl(item.url);
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            ...cardMediaSx,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.colors.border,
                            cursor: item.url ? 'pointer' : 'default',
                            '&:hover': item.url ? {
                              opacity: 0.9,
                              transition: 'opacity 0.2s',
                            } : {},
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.url) {
                              openUrl(item.url);
                            }
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 48, color: theme.colors.text.secondary, opacity: 0.5 }} />
                        </Box>
                      )}

                      {/* Título e informações do item */}
                      <CardContent sx={cardContentSx}>
                        <Typography
                          sx={titleOverlaySx}
                          title={item.title}
                        >
                          {item.title || 'Sem título'}
                        </Typography>
                        {(item.store || item.price) && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            {item.store && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.colors.text.secondary,
                                  fontSize: '0.6rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                }}
                              >
                                {item.store}
                              </Typography>
                            )}
                            {item.price && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.colors.primary,
                                  fontSize: '0.6rem',
                                  fontWeight: 'bold',
                                  marginLeft: item.store ? 1 : 0,
                                }}
                              >
                                R$ {item.price.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              /* List View */
              <Grid container spacing={1.5}>
                {displayItems.map((item) => (
                  <Grid size={{ xs: 12, md: 6 }} key={item.id}>
                    <Card
                      sx={{
                        display: 'flex',
                        height: 80,
                        borderRadius: radiusMedium,
                        boxShadow: 'none',
                        backgroundColor: cardBg,
                        border: `1px solid ${borderColor}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows?.medium || '0 4px 12px rgba(0,0,0,0.15)',
                          borderColor: primaryColor,
                        },
                      }}
                    >
                      {/* Thumbnail */}
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.url) openUrl(item.url);
                        }}
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          cursor: item.url ? 'pointer' : 'default',
                        }}
                      >
                        {item.imageUrl ? (
                          <CardMedia
                            component="img"
                            image={item.imageUrl}
                            alt={item.title || 'Item da wishlist'}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: item.imageUrl.includes('favicon') ? 'scale-down' : 'cover',
                              backgroundColor: item.imageUrl.includes('favicon') ? bg : 'transparent',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: borderColor,
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 28, color: theme.colors.text.secondary, opacity: 0.5 }} />
                          </Box>
                        )}
                      </Box>

                      {/* Content */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flex: 1,
                          px: 1.5,
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              color: textPrimary,
                              fontSize: '0.875rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.title || 'Sem título'}
                          </Typography>
                          {item.purchased && (
                            <Chip
                              label="Já é meu!"
                              size="small"
                              sx={{
                                mt: 0.25,
                                height: 18,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                backgroundColor: primaryColor,
                                color: theme.colors.text.contrast,
                              }}
                            />
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                            {item.store && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.colors.text.secondary,
                                  fontSize: '0.75rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.store}
                              </Typography>
                            )}
                            {item.price && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: primaryColor,
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                R$ {item.price.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(item);
                            }}
                            sx={{
                              color: textPrimary,
                              padding: '6px',
                              '&:hover': { color: primaryColor },
                            }}
                          >
                            <EditIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(item);
                            }}
                            sx={{
                              color: textPrimary,
                              padding: '6px',
                              '&:hover': { color: '#d32f2f' },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Typography
              sx={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
                mt: 4
              }}
            >
              Adicione itens à sua wishlist!
            </Typography>
          )}
        </Container>

        <BottomNavigation />
      </Box>

      <AddWishlistItemDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={addItem}
      />

      <EditWishlistItemDialog
        open={editDialog.open}
        item={editDialog.item}
        onClose={() => setEditDialog({ open: false, item: null })}
        onSave={handleSaveEdit}
      />

      <DeleteConfirmDialog
        open={deleteDialog.open}
        itemTitle={deleteDialog.item?.title || ''}
        onClose={() => setDeleteDialog({ open: false, item: null })}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

